import { useMemo, useRef, useState, type ReactNode } from 'react';
import type { PastelColor } from '../c2/Pill';

interface Scenario {
  slug: string;
  title: string;
  url: string;
  description: string;
  color: PastelColor;
  icon: string;
}

const SCENARIOS: Scenario[] = [
  {
    slug: 'roadmap',
    title: 'Q4 Product Roadmap',
    url: 'https://acme.notion.site/Q4-2026-Roadmap-a847b92c',
    description: 'The quarterly plan in Notion',
    color: 'sky',
    icon: '🗺️',
  },
  {
    slug: 'vacation',
    title: 'Request Time Off',
    url: 'https://app.rippling.com/time-off/request?policy=pto',
    description: 'Submit a vacation request',
    color: 'peach',
    icon: '🏖️',
  },
  {
    slug: 'oncall',
    title: 'On-call Runbook',
    url: 'https://acme.atlassian.net/wiki/spaces/ENG/On-Call-Runbook',
    description: 'What to do when things break',
    color: 'lemon',
    icon: '🚨',
  },
  {
    slug: 'newhire',
    title: 'New Hire Onboarding',
    url: 'https://docs.google.com/document/d/1Bf_OgC-9k2mXuZxE3nP/edit',
    description: 'First-week checklist',
    color: 'mint',
    icon: '🌱',
  },
];

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};

export default function InteractiveBrowserDemo() {
  const [value, setValue] = useState('');
  const [resolving, setResolving] = useState(false);
  const [arrived, setArrived] = useState<Scenario | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const match = useMemo(() => {
    if (!value.startsWith('go/')) return null;
    const slug = value.slice(3).trim().toLowerCase();
    if (!slug) return null;
    return SCENARIOS.find(l => l.slug === slug) ?? null;
  }, [value]);

  const handleGo = () => {
    if (!match) return;
    setResolving(true);
    setTimeout(() => {
      setResolving(false);
      setArrived(match);
    }, 900);
  };

  const reset = () => {
    setValue('');
    setArrived(null);
    setResolving(false);
    inputRef.current?.focus();
  };

  return (
    <div className="grid items-center" style={{ gridTemplateColumns: '1fr 1fr', gap: 32 }}>
      {/* LEFT: instructions + suggestions */}
      <div>
        <div
          className="inline-block uppercase font-semibold mb-4"
          style={{
            padding: '6px 14px',
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            borderRadius: 999,
            fontSize: 12,
            letterSpacing: '0.02em',
          }}
        >
          Try it yourself
        </div>
        <h3
          className="font-display font-extrabold text-ink m-0 mb-4"
          style={{ fontSize: 40, letterSpacing: '-0.02em', lineHeight: 1.1 }}
        >
          Type a go/ link.
          <br />
          Watch it resolve.
        </h3>
        <p className="text-base text-ink-soft leading-[1.6] m-0 mb-7">
          The browser on the right is pre-loaded with four go/ links. Type one in the address bar and press Enter to see how go/ links work.
        </p>
        <div
          className="font-semibold mb-2.5"
          style={{ fontSize: 13, color: 'var(--color-ink-soft)' }}
        >
          Try one of these:
        </div>
        <div className="flex flex-col" style={{ gap: 10 }}>
          {SCENARIOS.map(s => (
            <button
              key={s.slug}
              onClick={() => {
                setValue('go/' + s.slug);
                setArrived(null);
                setTimeout(() => inputRef.current?.focus(), 10);
              }}
              className="flex items-center bg-surface cursor-pointer text-left transition-all duration-150"
              style={{
                gap: 14,
                padding: '12px 16px',
                border: '1px solid var(--color-hairline)',
                borderRadius: 14,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--accent-solid)';
                e.currentTarget.style.transform = 'translateX(2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--color-hairline)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div
                className="flex items-center justify-center"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: pastelBg[s.color],
                  fontSize: 18,
                }}
              >
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="font-mono font-semibold"
                  style={{ fontSize: 13, color: 'var(--accent-deep)' }}
                >
                  go/{s.slug}
                </div>
                <div className="text-ink-soft mt-0.5" style={{ fontSize: 12 }}>
                  {s.title}
                </div>
              </div>
              <div style={{ color: 'var(--color-ink-faint)' }}>→</div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: interactive browser */}
      <div>
        <div
          className="bg-surface overflow-hidden"
          style={{
            border: '1px solid var(--color-hairline)',
            borderRadius: 28,
            boxShadow: 'var(--shadow-md)',
            minHeight: 400,
          }}
        >
          <div
            className="flex items-center bg-bg-alt"
            style={{
              gap: 10,
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-hairline)',
            }}
          >
            <div className="flex" style={{ gap: 6 }}>
              <div className="w-[11px] h-[11px] rounded-full" style={{ background: '#FF5F57' }} />
              <div className="w-[11px] h-[11px] rounded-full" style={{ background: '#FFBD2E' }} />
              <div className="w-[11px] h-[11px] rounded-full" style={{ background: '#28CA42' }} />
            </div>
            <span
              className="text-ink-muted font-medium ml-auto"
              style={{ fontSize: 11 }}
            >
              Chrome · Tidy Go Links extension active
            </span>
          </div>

          <div
            className="flex items-center"
            style={{
              gap: 10,
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-hairline)',
            }}
          >
            <div className="flex" style={{ gap: 4, color: 'var(--color-ink-faint)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1018 0 9 9 0 10-18 0M1 12h22" /></svg>
            </div>
            <div
              className="flex-1 flex items-center font-mono transition-[border-color,box-shadow] duration-200"
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: 'var(--color-bg-alt)',
                border: `1.5px solid ${resolving ? 'var(--accent-solid)' : 'var(--color-hairline)'}`,
                boxShadow: resolving ? '0 0 0 4px var(--accent-soft)' : 'none',
                fontSize: 14,
                color: 'var(--color-ink)',
                gap: 4,
              }}
            >
              {arrived ? (
                <span
                  className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono"
                  style={{ fontSize: 14 }}
                >
                  <span className="text-ink-muted">https://</span>
                  <span className="text-ink">{arrived.url.replace(/^https?:\/\//, '')}</span>
                </span>
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Type go/roadmap and press Enter"
                  value={value}
                  onChange={e => {
                    setValue(e.target.value);
                    if (arrived) setArrived(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && match) handleGo();
                  }}
                  className="flex-1 border-0 outline-none bg-transparent font-mono text-ink min-w-0"
                  style={{ fontSize: 14, padding: 0 }}
                />
              )}
              {match && !resolving && !arrived && (
                <button
                  onClick={handleGo}
                  className="border-0 cursor-pointer font-semibold"
                  style={{
                    background: 'var(--accent-solid)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontFamily: 'inherit',
                  }}
                >
                  Press Enter ↵
                </button>
              )}
            </div>
            <div
              aria-hidden="true"
              title="Tidy Go Links extension"
              className="flex items-center justify-center font-mono font-bold flex-shrink-0"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 11,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            >
              go/
            </div>
          </div>

          <div
            className="relative"
            style={{ minHeight: 280, background: 'var(--color-surface-alt)' }}
          >
            {!value && !arrived && (
              <div
                className="text-center text-ink-muted flex flex-col items-center"
                style={{ padding: 40, gap: 12 }}
              >
                <div
                  className="rounded-full flex items-center justify-center font-mono font-bold"
                  style={{
                    width: 80,
                    height: 80,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent-deep)',
                    fontSize: 26,
                  }}
                >
                  go/
                </div>
                <div
                  className="text-ink-soft"
                  style={{ fontSize: 14, maxWidth: 280 }}
                >
                  Type a go/ link above to see it resolve instantly.
                </div>
              </div>
            )}
            {value && !match && !arrived && !resolving && (
              <div
                className="text-center text-ink-muted"
                style={{ padding: 32 }}
              >
                <div className="text-3xl mb-2.5">🤔</div>
                <div className="text-ink-soft mx-auto leading-[1.55]" style={{ fontSize: 14, maxWidth: 320 }}>
                  {!value.startsWith('go/') ? (
                    <span>
                      Try starting with{' '}
                      <code
                        className="font-mono font-semibold"
                        style={{
                          background: 'var(--color-bg-alt)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          color: 'var(--accent-deep)',
                        }}
                      >
                        go/
                      </code>{' '}
                      — e.g.{' '}
                      <code
                        className="font-mono font-semibold"
                        style={{
                          background: 'var(--color-bg-alt)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          color: 'var(--accent-deep)',
                        }}
                      >
                        go/roadmap
                      </code>
                    </span>
                  ) : (
                    <span>
                      No match for{' '}
                      <code
                        className="font-mono"
                        style={{
                          background: 'var(--color-bg-alt)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          color: 'var(--color-ink)',
                        }}
                      >
                        {value}
                      </code>
                      . Try one of the suggestions on the left.
                    </span>
                  )}
                </div>
              </div>
            )}
            {value && match && !arrived && !resolving && (
              <div
                className="flex items-center"
                style={{ padding: 24, gap: 16, opacity: 0.7 }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: pastelBg[match.color],
                    fontSize: 24,
                  }}
                >
                  {match.icon}
                </div>
                <div>
                  <div
                    className="font-mono font-semibold mb-0.5"
                    style={{ fontSize: 12, color: 'var(--accent-deep)' }}
                  >
                    go/{match.slug}
                  </div>
                  <div
                    className="font-display font-bold text-ink"
                    style={{ fontSize: 20, letterSpacing: '-0.01em' }}
                  >
                    {match.title}
                  </div>
                  <div className="text-ink-soft mt-1" style={{ fontSize: 12 }}>
                    ↵ Press Enter to go here
                  </div>
                </div>
              </div>
            )}
            {resolving && (
              <div
                className="text-center flex flex-col items-center"
                style={{ padding: 40, gap: 16 }}
              >
                <div
                  className="rounded-full"
                  style={{
                    width: 56,
                    height: 56,
                    border: '3px solid var(--color-hairline)',
                    borderTopColor: 'var(--accent-solid)',
                    animation: 'c2spin 700ms linear infinite',
                  }}
                />
                <div className="text-ink-soft" style={{ fontSize: 14 }}>
                  Resolving{' '}
                  <strong className="font-mono" style={{ color: 'var(--accent-deep)' }}>
                    go/{match?.slug}
                  </strong>
                  ...
                </div>
              </div>
            )}
            {arrived && (
              <div style={{ animation: 'c2fadeSlide 400ms ease-out' }}>
                <DestinationMock scenario={arrived} />
              </div>
            )}
          </div>
        </div>
        {arrived && (
          <div className="text-center mt-4">
            <button
              onClick={reset}
              className="font-semibold cursor-pointer border-0"
              style={{
                background: 'var(--accent-solid)',
                padding: '10px 20px',
                borderRadius: 999,
                fontSize: 14,
                color: '#fff',
              }}
            >
              ↻ Try another
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes c2spin { to { transform: rotate(360deg); } }
        @keyframes c2fadeSlide { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

// ----------------------------------------------------------------
// DestinationMock — fake content per scenario
// ----------------------------------------------------------------

function DestinationMock({ scenario }: { scenario: Scenario }) {
  switch (scenario.slug) {
    case 'roadmap':
      return <RoadmapMock />;
    case 'vacation':
      return <VacationMock />;
    case 'oncall':
      return <OncallMock />;
    case 'newhire':
      return <NewhireMock />;
    default:
      return null;
  }
}

const mockShellPad = '20px 24px';

function RoadmapMock() {
  const cols: Array<{ month: string; tag: string; tagColor: string; items: string[] }> = [
    { month: 'October', tag: 'Shipped', tagColor: 'var(--color-mint)', items: ['Auth v2', 'Dashboard polish', 'Billing fix'] },
    { month: 'November', tag: 'In progress', tagColor: 'var(--color-lemon)', items: ['Payments v2', 'Mobile app beta'] },
    { month: 'December', tag: 'Planning', tagColor: 'var(--color-sky)', items: ['Onboarding redo', 'API v3'] },
  ];
  return (
    <div style={{ padding: mockShellPad }}>
      <div className="flex items-center text-ink-muted mb-2" style={{ fontSize: 11, gap: 6 }}>
        <span>Workspace</span>
        <span>›</span>
        <span>Product</span>
      </div>
      <div className="flex items-start mb-3" style={{ gap: 12 }}>
        <span style={{ fontSize: 26, lineHeight: 1 }}>🗺️</span>
        <div>
          <div className="font-display font-extrabold text-ink m-0" style={{ fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
            Q4 Product Roadmap
          </div>
          <div className="text-ink-muted mt-0.5" style={{ fontSize: 11 }}>
            Updated 2 days ago · Last edited by Priya
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3" style={{ gap: 8 }}>
        {cols.map(c => (
          <div
            key={c.month}
            style={{
              border: '1px solid var(--color-hairline)',
              borderRadius: 10,
              padding: 10,
              background: '#fff',
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-ink" style={{ fontSize: 12 }}>{c.month}</div>
              <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 999, background: c.tagColor, color: 'var(--color-ink)' }}>
                {c.tag}
              </span>
            </div>
            <div className="flex flex-col" style={{ gap: 4 }}>
              {c.items.map(item => (
                <div key={item} className="flex items-center" style={{ gap: 6, fontSize: 11, color: 'var(--color-ink-soft)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--color-hairline-strong)' }} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VacationMock() {
  return (
    <div style={{ padding: mockShellPad }}>
      <div className="flex items-center text-ink-muted mb-2" style={{ fontSize: 11, gap: 6 }}>
        <span>Time off</span>
        <span>›</span>
        <span>Request</span>
      </div>
      <div className="flex items-center mb-3" style={{ gap: 12 }}>
        <span style={{ fontSize: 26, lineHeight: 1 }}>🏖️</span>
        <div className="font-display font-extrabold text-ink m-0" style={{ fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
          Request Time Off
        </div>
      </div>
      <div
        className="flex items-center justify-between mb-3"
        style={{ background: 'var(--color-peach)', borderRadius: 10, padding: '10px 14px' }}
      >
        <div>
          <div className="text-ink-soft" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>PTO balance</div>
          <div className="font-display font-extrabold text-ink" style={{ fontSize: 22, lineHeight: 1.1 }}>14.5 days</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-ink-muted)' }}>Accrues 1.25/mo</div>
      </div>
      <div className="grid grid-cols-3 mb-3" style={{ gap: 8 }}>
        {[
          ['From', 'Dec 22'],
          ['To', 'Jan 2'],
          ['Total', '7 days'],
        ].map(([k, v]) => (
          <div key={k} style={{ border: '1px solid var(--color-hairline)', borderRadius: 8, padding: '6px 10px', background: '#fff' }}>
            <div className="text-ink-muted" style={{ fontSize: 10 }}>{k}</div>
            <div className="font-medium text-ink" style={{ fontSize: 13 }}>{v}</div>
          </div>
        ))}
      </div>
      <div
        className="flex items-center justify-between"
        style={{ fontSize: 11, color: 'var(--color-ink-soft)' }}
      >
        <span>Approver: Sam Cho (Manager)</span>
        <span
          style={{
            background: 'var(--accent-solid)',
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Submit request
        </span>
      </div>
    </div>
  );
}

function OncallMock() {
  const items: Array<{ done: boolean; text: ReactNode }> = [
    { done: true, text: 'Acknowledge the alert' },
    { done: true, text: 'Check #ops-incidents in Slack' },
    { done: false, text: 'Open Grafana: api-gateway dashboard' },
    {
      done: false,
      text: (
        <code style={{ background: 'var(--color-bg-alt)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>
          kubectl get pods -n prod
        </code>
      ),
    },
  ];
  return (
    <div style={{ padding: mockShellPad }}>
      <div className="flex items-center text-ink-muted mb-2" style={{ fontSize: 11, gap: 6 }}>
        <span>Engineering</span>
        <span>›</span>
        <span>Runbooks</span>
      </div>
      <div className="flex items-center mb-3" style={{ gap: 12 }}>
        <span style={{ fontSize: 26, lineHeight: 1 }}>🚨</span>
        <div className="font-display font-extrabold text-ink m-0" style={{ fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
          On-call Runbook
        </div>
      </div>
      <div
        className="flex items-center mb-3"
        style={{
          background: 'var(--color-peach)',
          borderLeft: '3px solid var(--color-peach-deep)',
          borderRadius: 6,
          padding: '8px 12px',
          gap: 8,
          fontSize: 12,
        }}
      >
        <span>⚡</span>
        <span className="font-medium text-ink">If PagerDuty is paging you, start at step 1.</span>
      </div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {items.map((it, i) => (
          <div key={i} className="flex items-center" style={{ gap: 8, fontSize: 12 }}>
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: it.done ? 'var(--color-mint-deep)' : 'transparent',
                border: it.done ? 'none' : '1.5px solid var(--color-hairline-strong)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {it.done ? '✓' : ''}
            </span>
            <span
              style={{
                color: it.done ? 'var(--color-ink-muted)' : 'var(--color-ink)',
                textDecoration: it.done ? 'line-through' : 'none',
              }}
            >
              {it.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewhireMock() {
  const sections: Array<{ heading: string; items: Array<{ done: boolean; text: string }> }> = [
    {
      heading: 'Day 1',
      items: [
        { done: true, text: 'Set up laptop & 2FA' },
        { done: true, text: 'Slack: join #welcome, #engineering' },
      ],
    },
    {
      heading: 'Week 1',
      items: [
        { done: false, text: '1:1 with manager' },
        { done: false, text: 'Read team culture deck' },
        { done: false, text: 'Pair with onboarding buddy' },
      ],
    },
  ];
  return (
    <div style={{ padding: mockShellPad }}>
      <div className="flex items-center mb-3" style={{ gap: 12 }}>
        <span style={{ fontSize: 26, lineHeight: 1 }}>🌱</span>
        <div>
          <div className="font-display font-extrabold text-ink m-0" style={{ fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
            New Hire Onboarding
          </div>
          <div className="text-ink-muted mt-0.5" style={{ fontSize: 11 }}>Last edited by HR · Shared with you</div>
        </div>
      </div>
      <div className="flex flex-col" style={{ gap: 10 }}>
        {sections.map(s => (
          <div key={s.heading}>
            <div className="font-semibold text-ink mb-1" style={{ fontSize: 12 }}>{s.heading}</div>
            <div className="flex flex-col" style={{ gap: 4 }}>
              {s.items.map(it => (
                <div key={it.text} className="flex items-center" style={{ gap: 8, fontSize: 12 }}>
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: it.done ? 'var(--color-mint-deep)' : 'transparent',
                      border: it.done ? 'none' : '1.5px solid var(--color-hairline-strong)',
                      color: '#fff',
                      fontSize: 9,
                      fontWeight: 700,
                    }}
                  >
                    {it.done ? '✓' : ''}
                  </span>
                  <span
                    style={{
                      color: it.done ? 'var(--color-ink-muted)' : 'var(--color-ink)',
                      textDecoration: it.done ? 'line-through' : 'none',
                    }}
                  >
                    {it.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
